import React, { useState, useEffect } from "react";
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, TextField, IconButton, Drawer, Card, CardContent, Typography, useTheme, useMediaQuery } from '@mui/material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx'; 
import AdminHeader from '../../components/admin/adminHeader.tsx'; 
import { Delete } from '@mui/icons-material'; 
import fashion_trends from '../../assets/fashion_trends.jpg';

const ManageProductList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for mobile menu toggle
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggleMenu = () => {
    setMobileOpen(prev => !prev);
  };

  const products = [
    {
      id: 1,
      image: fashion_trends,
      name: 'Green Sweater',
      onHand: 50,
      counted: 50,
      difference: 0,
    },
    {
      id: 2,
      image: fashion_trends,
      name: 'Blue T-Shirt',
      onHand: 50,
      counted: 55,
      difference: +5,
    },
    {
      id: 3,
      image: fashion_trends,
      name: 'White Casual Shirt',
      onHand: 25,
      counted: 26,
      difference: +1,
    },
  ];

  const initialExtended = Array.from({ length: 30 }, (_, i) => ({
    ...products[i % products.length],
    id: i + 1,
  }));

  const [productsData, setProductsData] = useState(initialExtended);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const productsPerPage = 10;

  const [showForm, setShowForm] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductImage, setNewProductImage] = useState('');
  const [newProductOnHand, setNewProductOnHand] = useState('');
  const [newProductCounted, setNewProductCounted] = useState('');

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const filteredProducts = productsData.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage) || 1;

  const currentProducts = filteredProducts.slice(
    (page - 1) * productsPerPage,
    page * productsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleAddProduct = () => {
    setShowForm(true);
  };

  const handleSaveProduct = () => {
    const newProduct = {
      id: productsData.length + 1,
      image: newProductImage || fashion_trends,
      name: newProductName,
      onHand: parseInt(newProductOnHand) || 0,
      counted: parseInt(newProductCounted) || 0,
      difference:
        (parseInt(newProductCounted) || 0) - (parseInt(newProductOnHand) || 0),
    };
    setProductsData(prev => [...prev, newProduct]);
    setShowForm(false);
    setNewProductName('');
    setNewProductImage('');
    setNewProductOnHand('');
    setNewProductCounted('');
  };

  const handleDeleteProduct = (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      setProductsData(prev => prev.filter(product => product.id !== id));
    }
  };

  // Mobile Card Component
  const ProductCard = ({ product, index }) => (
    <Card 
      sx={{ 
        mb: 2, 
        boxShadow: 2,
        '&:hover': { boxShadow: 4 }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Product Image */}
          <Box sx={{ flexShrink: 0 }}>
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          </Box>

          {/* Product Details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                mb: 1,
                wordBreak: 'break-word'
              }}
            >
              #{(page - 1) * productsPerPage + index + 1} - {product.name}
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  On Hand
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {product.onHand}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  Counted
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {product.counted}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  Difference
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '14px', 
                    fontWeight: 600,
                    color: product.difference >= 0 ? 'green' : 'red',
                  }}
                >
                  {product.difference}
                </Typography>
              </Box>
              
              <IconButton
                color="error"
                onClick={() => handleDeleteProduct(product.id)}
                sx={{ 
                  backgroundColor: '#ffebee',
                  '&:hover': { backgroundColor: '#ffcdd2' },
                  width: 40,
                  height: 40
                }}
              >
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Container for sidebar + main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box
          sx={{
            width: '250px',
            flexShrink: 0,
            overflowY: 'auto',
            display: { xs: 'none', sm: 'block' }
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={() => {}} />
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={() => {}} />
        </Drawer>

        {/* Main Content area with Header + Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with toggle button */}
          <AdminHeader onToggleMenu={handleToggleMenu} />
          
          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: { xs: 1, sm: 2 },
              paddingTop: '10px',
              backgroundColor: '#f5f5f5',
            }}
          >
            {/* Wrapper for responsiveness */}
            <Box
              sx={{
                maxWidth: '100%',
                width: '100%',
                boxSizing: 'border-box',
                px: { xs: 1, sm: 2 },
              }}
            >
              {/* Controls: Add and Search */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: 2,
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#C32032',
                    '&:hover': { backgroundColor: '#a01626' },
                    width: { xs: '100%', sm: 'auto' },
                    fontSize: { xs: '14px', sm: '16px' },
                    height: { xs: 40, sm: 'auto' }
                  }}
                  onClick={handleAddProduct}
                >
                  Add new product
                </Button>
                <TextField
                  size="small"
                  placeholder="Search..."
                  variant="outlined"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '16px' }
                    }
                  }}
                />
              </Box>

              {/* Show form for new product */}
              {showForm && (
                <Box
                  sx={{
                    mb: 2,
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: 1,
                    maxWidth: 600,
                    width: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '18px', sm: '20px' } }}>
                    Add New Product
                  </Typography>
                  <TextField
                    label="Product Name"
                    fullWidth
                    sx={{ 
                      mb: 2,
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '14px', sm: '16px' }
                      }
                    }}
                    value={newProductName}
                    onChange={(e) => setNewProductName(e.target.value)}
                  />
                  <TextField
                    label="Image URL"
                    fullWidth
                    sx={{ 
                      mb: 2,
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '14px', sm: '16px' }
                      }
                    }}
                    value={newProductImage}
                    onChange={(e) => setNewProductImage(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                    <TextField
                      label="On Hand"
                      type="number"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '14px', sm: '16px' }
                        }
                      }}
                      value={newProductOnHand}
                      onChange={(e) => setNewProductOnHand(e.target.value)}
                    />
                    <TextField
                      label="Counted"
                      type="number"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '14px', sm: '16px' }
                        }
                      }}
                      value={newProductCounted}
                      onChange={(e) => setNewProductCounted(e.target.value)}
                    />
                  </Box>
                  {/* Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveProduct}
                      sx={{ 
                        flex: 1,
                        fontSize: { xs: '14px', sm: '16px' },
                        height: { xs: 40, sm: 'auto' }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowForm(false)}
                      sx={{ 
                        flex: 1,
                        fontSize: { xs: '14px', sm: '16px' },
                        height: { xs: 40, sm: 'auto' }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Conditional Rendering: Table for Desktop, Cards for Mobile */}
              {!isMobile ? (
                // Desktop Table View
                <TableContainer
                  component={Paper}
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                  }}
                >
                  <Table aria-label="product table" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>No</TableCell>
                        <TableCell>Image</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>On Hand</TableCell>
                        <TableCell>Counted</TableCell>
                        <TableCell>Difference</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentProducts.map((product, index) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            {(page - 1) * productsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>{product.onHand}</TableCell>
                          <TableCell>{product.counted}</TableCell>
                          <TableCell
                            style={{
                              color: product.difference >= 0 ? 'green' : 'red',
                            }}
                          >
                            {product.difference}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                // Mobile Card View
                <Box sx={{ width: '100%' }}>
                  {currentProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      index={index}
                    />
                  ))}
                </Box>
              )}

              {/* Pagination */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  shape="rounded"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ManageProductList;